import { supabase } from '@/lib/supabase';
import { Story, CreateStoryForm, ApiResponse, PaginatedResponse } from '@/types/database';
import { aiService, StoryGenerationRequest } from '@/lib/ai';
import { storageService } from '@/lib/storage';

export const storiesApi = {
  // Get all stories for the current user
  async getUserStories(): Promise<ApiResponse<Story[]>> {
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data || [],
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch stories',
        success: false
      };
    }
  },

  // Get a single story by ID
  async getStory(id: string): Promise<ApiResponse<Story>> {
    try {
      const { data, error } = await supabase
        .from('stories')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to fetch story',
        success: false
      };
    }
  },

  // Create a new story
  async createStory(storyFormData: CreateStoryForm): Promise<ApiResponse<Story>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Check subscription limits first
      const { data: usage } = await supabase
        .rpc('get_user_usage', { p_user_id: user.id });

      if (usage && usage[0]) {
        const currentUsage = usage[0];
        if (currentUsage.stories_count >= currentUsage.max_stories) {
          return {
            error: `You've reached your story limit (${currentUsage.max_stories}). Please upgrade your plan to create more stories.`,
            success: false
          };
        }
      }

      // Generate story with AI
      const aiRequest: StoryGenerationRequest = {
        prompt: storyFormData.prompt,
        genre: storyFormData.genre,
        ageGroup: storyFormData.age_group,
        storyLength: storyFormData.story_length,
        illustrationStyle: storyFormData.illustration_style,
        mainCharacter: storyFormData.main_character
      };

      const generatedStory = await aiService.generateStory(aiRequest);

      // Create the story first to get the story ID
      const { data: storyData, error: storyError } = await supabase
        .from('stories')
        .insert({
          user_id: user.id,
          title: generatedStory.title,
          description: storyFormData.description,
          content: {
            pages: generatedStory.pages.map(page => ({
              page_number: page.pageNumber,
              text: page.text,
              image_prompt: page.imagePrompt,
              illustration_style: page.illustrationStyle,
              image_url: page.imageUrl
            })),
            metadata: {
              total_pages: generatedStory.metadata.totalPages,
              estimated_read_time: generatedStory.metadata.estimatedReadTime,
              generated_at: generatedStory.metadata.generatedAt
            }
          },
          genre: storyFormData.genre,
          age_group: storyFormData.age_group,
          illustration_style: storyFormData.illustration_style,
          story_length: storyFormData.story_length,
          main_character: storyFormData.main_character,
          read_time_minutes: generatedStory.metadata.estimatedReadTime
        })
        .select()
        .single();

      if (storyError) throw storyError;

      // Now process and upload images to Supabase Storage
      const processedPages = await aiService.processAndUploadImages(generatedStory.pages, storyData.id);

      // Update the story with the processed pages (now with Supabase Storage URLs)
      const { data: updatedStory, error: updateError } = await supabase
        .from('stories')
        .update({
          content: {
            pages: processedPages.map(page => ({
              page_number: page.pageNumber,
              text: page.text,
              image_prompt: page.imagePrompt,
              illustration_style: page.illustrationStyle,
              image_url: page.imageUrl
            })),
            metadata: {
              total_pages: generatedStory.metadata.totalPages,
              estimated_read_time: generatedStory.metadata.estimatedReadTime,
              generated_at: generatedStory.metadata.generatedAt
            }
          }
        })
        .eq('id', storyData.id)
        .select()
        .single();

      if (updateError) throw updateError;

      return {
        data: updatedStory,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to create story',
        success: false
      };
    }
  },

  // Update a story
  async updateStory(id: string, updates: Partial<Story>): Promise<ApiResponse<Story>> {
    try {
      const { data, error } = await supabase
        .from('stories')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to update story',
        success: false
      };
    }
  },

  // Delete a story
  async deleteStory(id: string): Promise<ApiResponse<void>> {
    try {
      // Delete the story from the database
      const { error } = await supabase
        .from('stories')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clean up associated images from storage
      await storageService.deleteStoryImages(id);

      return {
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to delete story',
        success: false
      };
    }
  },

  // Publish a story
  async publishStory(id: string): Promise<ApiResponse<Story>> {
    try {
      const { data, error } = await supabase
        .from('stories')
        .update({
          is_published: true,
          published_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to publish story',
        success: false
      };
    }
  },

  // Unpublish a story
  async unpublishStory(id: string): Promise<ApiResponse<Story>> {
    try {
      const { data, error } = await supabase
        .from('stories')
        .update({
          is_published: false,
          published_at: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      return {
        data,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to unpublish story',
        success: false
      };
    }
  },

  // Get user's story usage
  async getUserUsage(): Promise<ApiResponse<any>> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .rpc('get_user_usage', { p_user_id: user.id });

      if (error) throw error;

      return {
        data: data?.[0] || null,
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to get usage',
        success: false
      };
    }
  },

  // Generate story with AI (now uses real AI)
  async generateStory(prompt: string, preferences: any): Promise<ApiResponse<any>> {
    try {
      const aiRequest: StoryGenerationRequest = {
        prompt,
        genre: preferences.genre || 'adventure',
        ageGroup: preferences.age_group || '3-5',
        storyLength: preferences.story_length || 'medium',
        illustrationStyle: preferences.illustration_style || 'watercolor'
      };

      const generatedStory = await aiService.generateStory(aiRequest);

      return {
        data: {
          title: generatedStory.title,
          content: {
            pages: generatedStory.pages.map(page => ({
              page_number: page.pageNumber,
              text: page.text,
              image_prompt: page.imagePrompt,
              illustration_style: page.illustrationStyle
            })),
            metadata: {
              total_pages: generatedStory.metadata.totalPages,
              estimated_read_time: generatedStory.metadata.estimatedReadTime,
              generated_at: generatedStory.metadata.generatedAt
            }
          }
        },
        success: true
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : 'Failed to generate story',
        success: false
      };
    }
  }
}; 